import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Table,
  Header,
  TextFilter,
  Spinner,
  Button,
  Box,
  Link,
  Container,
  SpaceBetween,
  Grid,
  Pagination,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { StateContext, DispatchContext } from './context/spotcp-context';
import { getJSClient } from '../js_client';
import '../../styles/spotcp.css';
import { InfoLink, SpotcpAsinAnalysisInfo, ExternalLinkItem } from './info';
import { EmptyState } from './elements';
import { SCROLL_TO_REF } from './actions/actionTypes';

const asinAnalysisMainColumnDefinitions = (props) => {
  const { setAsinDetail, setSelectedAsin, asinDetailRef } = props;
  const dispatch = useContext(DispatchContext);
  const onLinkClicked = (item) => {
    setAsinDetail(item.accuracyDetail);
    setSelectedAsin(item.asin);
    dispatch({ type: SCROLL_TO_REF, payload: { ref: asinDetailRef } });
  };
  return [
    {
      id: 'asin',
      header: 'ASIN',
      minWidth: 45,
      sortingField: 'asin',
      cell: (item) => {
        return (
          <Box textAlign="left">
            <Link onFollow={() => onLinkClicked(item)}>{item.asin}</Link>
          </Box>
        );
      },
    },
    {
      id: 'gl',
      header: 'Product Line',
      minWidth: 30,
      sortingField: 'gl',
      cell: (item) => {
        return <Box textAlign="left">{item.gl}</Box>;
      },
    },
    {
      id: 'totalNetVar',
      header: 'Net CP Net Var',
      minWidth: 30,
      sortingField: 'totalNetVar',
      cell: (item) => {
        let value = Math.round(item.totalNetVar * 10000) / 10000;
        let colorText = value >= 0 ? 'inherit' : 'text-status-error';
        return (
          <Box textAlign="right" color={colorText}>
            {value || '-'}
          </Box>
        );
      },
    },
    {
      id: 'shipCount',
      header: 'Shipment Item Counts',
      minWidth: 30,
      sortingField: 'shipCount',
      cell: (item) => <Box textAlign="right">{item.shipCount || '-'}</Box>,
    },
    {
      id: 'pGms',
      header: 'Product GMS / Unit Value',
      minWidth: 45,
      sortingField: 'pGms',
      cell: (item) => <Box textAlign="right">{Math.round(item.pGms * 10000) / 10000 || '-'}</Box>,
    },
    {
      id: 'accuracy',
      header: 'Total Accuracy',
      minWidth: 45,
      sortingField: 'accuracy',
      cell: (item) => <Box textAlign="right">{Math.round(item.accuracy * 100) / 100 || '-'}</Box>,
    },
  ];
};

const detailsColumnDefinitions = [
  {
    id: 'component',
    header: 'Component',
    minWidth: 45,
    cell: (item) => {
      return (
        <Box textAlign="left" fontWeight="bold">
          {item.component}
        </Box>
      );
    },
  },
  {
    id: 'netVar',
    header: 'Net Variance / Unit Value',
    minWidth: 45,
    sortingField: 'netVar',
    cell: (item) => {
      let value = Math.round(item.netVar * 10000) / 10000;
      let colorText = value >= 0 ? 'inherit' : 'text-status-error';
      return (
        <Box textAlign="right" color={colorText}>
          {value || '-'}
        </Box>
      );
    },
  },
  {
    id: 'accuracy',
    header: 'Accuracy %',
    minWidth: 45,
    sortingField: 'accuracy',
    cell: (item) => <Box textAlign="right">{Math.round(item.accuracy * 100) / 100 || '-'}</Box>,
  },
  {
    id: 'weight',
    header: 'Weight %',
    minWidth: 45,
    sortingField: 'weight',
    cell: (item) => <Box textAlign="right">{Math.round(item.weight * 10000) / 100 || '-'}</Box>,
  },
];

export const AsinAnalysis = (props) => {
  let client;
  const { updateTools } = props;
  const [loading, setLoading] = useState(true);
  const [asinDetail, setAsinDetail] = useState(null);
  const [selectedAsin, setSelectedAsin] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const asinDetailRef = useRef(null);
  const {
    request,
    currentExecutionId,
    currentNextToken,
    currentBin,
    currentComponent,
    currentHeader,
    currentSource,
    asinData,
  } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  useEffect(() => {
    client = getJSClient();
  });

  useEffect(() => {
    if (!asinData) {
      setAsinDetail(null);
      setSelectedAsin(null);
    }
    setLoading(asinData ? false : true);
    setLoadingMore(false);
  }, [asinData]);

  const { items, actions, collectionProps, filterProps, paginationProps } = useCollection(asinData || [], {
    filtering: {
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can’t find a match."
          action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
        />
      ),
    },
    pagination: { pageSize: 30 },
    sorting: {}, // use default so pass empty object
  });

  const asinDetailcollection = useCollection(asinDetail || [], {
    sorting: {},
  });

  const loadMore = () => {
    setLoadingMore(true);
    client.runAsinAnalysis(dispatch, {
      ...request,
      useLA: false,
      executionId: currentExecutionId,
      nextToken: currentNextToken,
      component: currentComponent,
      bin: currentBin,
      header: currentHeader,
    });
  };

  return (
    <Container
      header={
        <Header variant="h2" info={<InfoLink onFollow={() => updateTools(<SpotcpAsinAnalysisInfo />)} />}>
          ASIN Analysis
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Grid
          gridDefinition={[
            { colspan: { l: '7', m: '7', default: '12' } },
            { colspan: { l: '5', m: '5', default: '12' } },
          ]}
        >
          <Table
            {...collectionProps}
            columnDefinitions={asinAnalysisMainColumnDefinitions({ setAsinDetail, setSelectedAsin, asinDetailRef })}
            items={items || []}
            loading={loading}
            wrapLines
            loadingText="Loading Data..."
            filter={<TextFilter {...filterProps} />}
            pagination={<Pagination {...paginationProps} />}
            header={
              <Header variant="h2">{`Bin: ${currentBin} in ${
                currentComponent == 'total' ? 'Net CP' : currentComponent
              } component using ${currentSource} data`}</Header>
            }
            footer={
              currentNextToken ? (
                loadingMore ? (
                  <Spinner size="normal" />
                ) : (
                  <Box textAlign="center">
                    <Link onFollow={loadMore}>View More</Link>
                  </Box>
                )
              ) : null
            }
          ></Table>

          <div ref={asinDetailRef}>
            <Table
              {...asinDetailcollection.collectionProps}
              columnDefinitions={detailsColumnDefinitions}
              items={asinDetailcollection.items || []}
              header={
                <Header
                  variant="h2"
                  description={
                    selectedAsin ? (
                      <ExternalLinkItem
                        href={`https://www.amazon.com/s?k=${selectedAsin}`}
                        text="check ASIN in amazon.com"
                      />
                    ) : null
                  }
                >
                  {selectedAsin ? `Details for ASIN ${selectedAsin}` : null}
                </Header>
              }
              wrapLines
              empty={<EmptyState title="No ASIN data to show" subtitle="Please select an AISN in right table" />}
            ></Table>
          </div>
        </Grid>
      </SpaceBetween>
    </Container>
  );
};



// WEBPACK FOOTER //
// ./src/components/spotcp/asin-analysis.jsx